import React, { useState } from "react";
import {
  Box,
  IconButton,
  Tooltip,
  Modal,
  Paper,
  Typography,
  TextField,
  Button,
} from "@mui/material";
import { RiClipboardLine, RiCodeView } from "@remixicon/react";

interface KuebikoQueryCellProps {
  queryTemplate: string; // The dynamic query template
  columnMappings: Record<string, string>; // Dynamic mappings of placeholders to column values
  tooltip?: string; // Optional tooltip for buttons
}

const modalStyle = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "80%",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
};

const KuebikoQueryCell: React.FC<KuebikoQueryCellProps> = ({
  queryTemplate,
  columnMappings,
  tooltip = "Kuebiko Query",
}) => {
  const [open, setOpen] = useState(false);

  // Check if all placeholders have values
  const canGenerateQuery = Object.values(columnMappings).every(
    (value) => value !== undefined && value !== null
  );

  // Generate the query by replacing placeholders with column values
  const query = Object.entries(columnMappings).reduce(
    (result, [placeholder, value]) =>
      result.replace(new RegExp(placeholder, "g"), value || ""),
    queryTemplate
  );

  const handleCopyQuery = () => {
    navigator.clipboard.writeText(query.trim());
  };

  return (
    <Box display="flex" alignItems="center" gap={1}>
      {canGenerateQuery && (
        <>
          {/* Copy Query Button */}
          <Tooltip title={`Copy ${tooltip}`}>
            <IconButton onClick={handleCopyQuery} color="primary" size="small">
              <RiClipboardLine />
            </IconButton>
          </Tooltip>
          {/* View Query Button */}
          <Tooltip title={`View ${tooltip}`}>
            <IconButton
              onClick={() => setOpen(true)}
              color="primary"
              size="small"
            >
              <RiCodeView />
            </IconButton>
          </Tooltip>

          {/* Modal for Viewing Query */}
          <Modal open={open} onClose={() => setOpen(false)}>
            <Paper sx={modalStyle}>
              <Typography variant="h6" gutterBottom>
                {tooltip}
              </Typography>
              <TextField
                multiline
                fullWidth
                minRows={8}
                value={query.trim()}
                variant="outlined"
                sx={{
                  bgcolor: "#f5f5f5",
                  p: 2,
                  borderRadius: 1,
                  fontFamily: "monospace",
                  fontSize: "0.9rem",
                  lineHeight: "1.5",
                  overflowX: "auto",
                  "& .MuiInputBase-root": {
                    fontFamily: "monospace",
                  },
                }}
              />
              <Box textAlign="right" mt={2}>
                <Button onClick={() => setOpen(false)} variant="contained">
                  Close
                </Button>
              </Box>
            </Paper>
          </Modal>
        </>
      )}
    </Box>
  );
};

export default KuebikoQueryCell;
